import React, { useEffect, useRef, useState } from 'react';
import {
  Toolbar,
  Separator,
  createButton,
  createDropdown,
  useEditorState,
} from 'react-simple-wysiwyg';
import './procurementDetail.css';

/*
  Shared toolbar for the react-simple-wysiwyg editors on the procurement
  detail page. Same commands as the library defaults, but with Remix icons,
  logical groups and a compact highlight picker. Styles live in
  procurementDetail.css under `.pd-rte`.

  Usage:
    <Editor ...>
      <RichTextToolbar highlight html>
        {optional extra controls, e.g. a template select}
      </RichTextToolbar>
    `highlight` adds the highlight picker, `html` the HTML-source toggle.
    </Editor>
*/

const icon = name => <i className={name} aria-hidden='true'></i>;

const BtnUndo = createButton('Undo', icon('ri-arrow-go-back-line'), 'undo');
const BtnRedo = createButton('Redo', icon('ri-arrow-go-forward-line'), 'redo');
const BtnBold = createButton('Bold', icon('ri-bold'), 'bold');
const BtnItalic = createButton('Italic', icon('ri-italic'), 'italic');
const BtnUnderline = createButton(
  'Underline',
  icon('ri-underline'),
  'underline'
);
const BtnStrike = createButton(
  'Strikethrough',
  icon('ri-strikethrough'),
  'strikeThrough'
);
const BtnBulletList = createButton(
  'Bullet list',
  icon('ri-list-unordered'),
  'insertUnorderedList'
);
const BtnNumberedList = createButton(
  'Numbered list',
  icon('ri-list-ordered'),
  'insertOrderedList'
);
const BtnLink = createButton(
  'Insert / remove link',
  icon('ri-link'),
  ({ $selection }) => {
    if ($selection?.nodeName === 'A') {
      document.execCommand('unlink');
      return;
    }
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL', 'https://');
    if (url && url !== 'https://') {
      document.execCommand('createLink', false, url);
    }
  }
);
const BtnClear = createButton(
  'Clear formatting',
  icon('ri-format-clear'),
  'removeFormat'
);

const BtnTextStyle = createDropdown('Text style', [
  ['Normal text', 'formatBlock', 'DIV'],
  ['Heading 1', 'formatBlock', 'H1'],
  ['Heading 2', 'formatBlock', 'H2'],
  ['Code block', 'formatBlock', 'PRE'],
]);

const HIGHLIGHTS = [
  { label: 'Yellow', value: '#fff176' },
  { label: 'Green', value: '#b9f6ca' },
  { label: 'Blue', value: '#b3e5fc' },
  { label: 'Pink', value: '#f8bbd0' },
  { label: 'Orange', value: '#ffcc80' },
];

function BtnHighlight() {
  const { $el, htmlMode } = useEditorState();
  const [open, setOpen] = useState(false);
  const [color, setColor] = useState(HIGHLIGHTS[0].value);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = e => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (htmlMode) return null;

  // Mouse-down handlers keep the editor's text selection alive; execCommand
  // fires an input event, so the editor's onChange picks up the change.
  const apply = value => e => {
    e.preventDefault();
    if (!$el?.contains(document.activeElement)) $el?.focus();
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('hiliteColor', false, value);
    if (value !== 'transparent') setColor(value);
    setOpen(false);
  };

  return (
    <div className='pd-rte__hl' ref={wrapRef}>
      <button
        type='button'
        className='rsw-btn pd-rte__hl-main'
        title='Highlight'
        tabIndex={-1}
        onMouseDown={apply(color)}
      >
        <i className='ri-mark-pen-line' aria-hidden='true'></i>
        <span className='pd-rte__hl-bar' style={{ background: color }} />
      </button>
      <button
        type='button'
        className='rsw-btn pd-rte__hl-caret'
        title='Highlight colour'
        aria-haspopup='true'
        aria-expanded={open}
        data-active={open}
        tabIndex={-1}
        onMouseDown={e => {
          e.preventDefault();
          setOpen(o => !o);
        }}
      >
        <i className='ri-arrow-down-s-line' aria-hidden='true'></i>
      </button>
      {open && (
        <div className='pd-rte__hl-pop' role='menu'>
          <div className='pd-rte__hl-swatches'>
            {HIGHLIGHTS.map(h => (
              <button
                key={h.value}
                type='button'
                role='menuitem'
                className='pd-rte__swatch'
                title={h.label}
                aria-label={h.label}
                data-active={h.value === color}
                style={{ background: h.value }}
                onMouseDown={apply(h.value)}
              />
            ))}
          </div>
          <button
            type='button'
            role='menuitem'
            className='pd-rte__hl-none'
            onMouseDown={apply('transparent')}
          >
            <i className='ri-close-circle-line' aria-hidden='true'></i>
            No highlight
          </button>
        </div>
      )}
    </div>
  );
}

// HTML source toggle; unlike the other buttons it stays visible in HTML mode.
function BtnHtml() {
  const { htmlMode, update } = useEditorState();
  return (
    <button
      type='button'
      className='rsw-btn'
      title={htmlMode ? 'Back to editor' : 'Edit HTML source'}
      data-active={htmlMode}
      tabIndex={-1}
      onClick={() => update({ htmlMode: !htmlMode })}
    >
      <i className='ri-code-s-slash-line' aria-hidden='true'></i>
    </button>
  );
}

const RichTextToolbar = ({ highlight = false, html = false, children }) => (
  <Toolbar className='rsw-toolbar pd-rte'>
    <BtnUndo />
    <BtnRedo />
    <Separator />
    <BtnTextStyle />
    <Separator />
    <BtnBold />
    <BtnItalic />
    <BtnUnderline />
    <BtnStrike />
    {highlight && <BtnHighlight />}
    <Separator />
    <BtnBulletList />
    <BtnNumberedList />
    <Separator />
    <BtnLink />
    <BtnClear />
    {html && <BtnHtml />}
    {children && (
      <>
        <Separator />
        <div className='pd-rte__extra'>{children}</div>
      </>
    )}
  </Toolbar>
);

export default RichTextToolbar;
